import {
  formatAud,
  formatPercentFromPct,
} from '@/pages/blueprint-lite/utils/formatters';

import type {
  CashflowResult,
  PropertyType,
  PropertyUse,
} from '../../../assessment/types';
import s from '../styles/dashboard.module.css';

interface Props {
  cashflow: CashflowResult;
  propertyUse: PropertyUse;
  propertyType: PropertyType;
  state: string;
  rentalYieldPct: number;
  hasPurchase: boolean;
}

const LedgerRow = ({
  label,
  value,
  tone,
  className,
}: {
  label: string;
  value: number;
  tone?: 'pos' | 'neg';
  className?: string;
}) => (
  <div className={`${s.ledgerRow} ${className ?? ''}`}>
    <span className={s.ledgerRowKey}>{label}</span>
    <span className={`${s.ledgerRowVal} ${tone ? s[tone] : ''}`}>
      {formatAud(value)}
    </span>
  </div>
);

const CashflowCard = ({
  cashflow,
  propertyUse,
  propertyType,
  state,
  rentalYieldPct,
  hasPurchase,
}: Props) => {
  const isInvestment = propertyUse === 'investment';
  const heading = isInvestment ? 'Investment property' : 'Owner-occupied';
  const meta = `${state} · ${propertyType.charAt(0).toUpperCase() + propertyType.slice(1)}`;

  return (
    <>
      <header className={s.sheetHd}>
        <h3 className={s.sheetTitle}>{heading} — annual breakdown</h3>
        <span className={s.sheetMeta}>{meta}</span>
      </header>

      {isInvestment && cashflow.annualRent > 0 && (
        <LedgerRow
          label={`Estimated rental income (${hasPurchase ? `${formatPercentFromPct(rentalYieldPct)} yield` : 'existing rent'})`}
          value={cashflow.annualRent}
          tone="pos"
        />
      )}
      <LedgerRow
        label="Annual mortgage repayments (P&I)"
        value={-cashflow.annualRepay}
        tone="neg"
      />
      {isInvestment && cashflow.managementFee > 0 && (
        <LedgerRow
          label="Property management fees (8%)"
          value={-cashflow.managementFee}
          tone="neg"
        />
      )}
      <LedgerRow
        label="Rates, insurance &amp; maintenance (est.)"
        value={-(cashflow.ratesInsurance + cashflow.maintenance)}
        tone="neg"
      />
      {cashflow.strata > 0 && (
        <LedgerRow
          label="Strata / body corporate (est.)"
          value={-cashflow.strata}
          tone="neg"
        />
      )}

      {isInvestment && cashflow.preTaxCashflow < 0 && (
        <>
          <LedgerRow
            label="Pre-tax net cashflow"
            value={cashflow.preTaxCashflow}
            tone="neg"
            className={s.ledgerSubtotal}
          />
          <LedgerRow
            label={`+ Negative-gearing tax offset (est. ${formatPercentFromPct(cashflow.marginalRate * 100, 0)})`}
            value={cashflow.negGearingOffset}
            tone="pos"
            className={s.ledgerOffset}
          />
        </>
      )}

      <div className={s.ledgerTotal}>
        <span className={s.ledgerTotalKey}>After-tax cashflow</span>
        <span
          className={`${s.ledgerTotalVal} ${cashflow.afterTaxCashflow >= 0 ? s.pos : s.neg}`}
        >
          {formatAud(cashflow.afterTaxCashflow)}/yr ·{' '}
          {formatAud(cashflow.afterTaxCashflow / 12)}/mo
        </span>
      </div>

      <p className={s.sheetFoot}>
        Cashflow estimates use indicative figures. Tax offset estimated at{' '}
        {formatPercentFromPct(cashflow.marginalRate * 100, 0)} marginal rate —
        actual deductions depend on the full tax position. Verify with an ODIN
        tax agent before reliance.
      </p>
    </>
  );
};

export default CashflowCard;
